import { useMutation } from '@tanstack/react-query'
import { useToast } from 'src/shared/hooks/use-toast'
import { useLocalization } from 'src/shared/hooks/useLocalization'

import { getTestNotificationsFromStorage } from 'src/shared/consts/testNotifications'
import { ServerRoutes } from './serverRoutes'

export type OrderNotificationParams = {
  quotePublicId: string
  headerBadge: string
  headerTitle: string
  message: string
  buttonText: string
}

export type ShippingStatusParams = {
  quotePublicId: string
  body?: string
}

export type ActorRole = 'customer' | 'admin'

export type NotificationKind = 'lifecycle' | 'chat' | 'shipping_status'

export type OrderNotificationRequest = {
  kind: NotificationKind
  actorRole: ActorRole
  subject: string
  orderNotification?: OrderNotificationParams
  shippingStatus?: ShippingStatusParams
}

export const useOrderNotifications = () => {
  const toast = useToast()
  const { t } = useLocalization()

  return useMutation({
    mutationFn: async (props: OrderNotificationRequest) => {
      const response = await fetch(
        import.meta.env.VITE_BE_SERVER_URL + ServerRoutes.notificationsOrder,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            kind: props.kind,
            actorRole: props.actorRole,
            subject: props.subject,
            test_notifications: getTestNotificationsFromStorage(),
            ...(props.orderNotification != null && {
              orderNotification: props.orderNotification,
            }),
            ...(props.shippingStatus != null && {
              shippingStatus: props.shippingStatus,
            }),
          }),
        }
      )

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(
          errorData.msg || t('Failed to send email', 'Kunde inte skicka e-post')
        )
      }

      return await response.json()
    },
    onError: (error: Error) => {
      toast.toast({
        title: t('Something went wrong', 'Något gick fel'),
        description: error.message,
        variant: 'destructive',
        duration: 5000,
      })
    },
  })
}
